/**
 * Cross-View Data Consistency Validation Utilities
 * 
 * Validates data consistency between family list view and family detail view
 * to ensure data integrity across different views of the same data.
 * 
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5
 */

/**
 * Validate cross-view data consistency between family list and family detail
 * Checks that tutee count, revenue, and active subject count match between views
 * 
 * @param {object} familyListData - Family data from list view (GET /api/admin/families)
 * @param {object} familyDetailData - Family data from detail view (GET /api/admin/families/:familyNumber)
 * @returns {object} { isConsistent: boolean, warnings: string[] }
 */
export const validateCrossViewConsistency = (familyListData, familyDetailData) => {
  const warnings = [];

  // Validate inputs
  if (!familyListData || typeof familyListData !== 'object') {
    warnings.push('Family list data is invalid or missing');
    return { isConsistent: false, warnings };
  }

  if (!familyDetailData || typeof familyDetailData !== 'object') {
    warnings.push('Family detail data is invalid or missing');
    return { isConsistent: false, warnings };
  }

  // Ensure we're comparing the same family
  if (familyListData.familyNumber !== familyDetailData.familyNumber) {
    warnings.push(
      `Family number mismatch: list view has ${familyListData.familyNumber}, detail view has ${familyDetailData.familyNumber}`
    );
    return { isConsistent: false, warnings };
  }

  // Requirement 14.1: Check tutee count consistency
  if (familyListData.totalTutees !== familyDetailData.totalTutees) {
    warnings.push(
      `Tutee count mismatch for family ${familyListData.familyNumber}: ` +
      `list view shows ${familyListData.totalTutees}, detail view shows ${familyDetailData.totalTutees}`
    );
  }

  // Also check that tutee count matches actual tutees array length
  if (familyListData.tutees && familyListData.tutees.length !== familyListData.totalTutees) {
    warnings.push(
      `List view tutee count (${familyListData.totalTutees}) does not match tutees array length (${familyListData.tutees.length})`
    );
  }

  if (familyDetailData.tutees && familyDetailData.tutees.length !== familyDetailData.totalTutees) {
    warnings.push(
      `Detail view tutee count (${familyDetailData.totalTutees}) does not match tutees array length (${familyDetailData.tutees.length})`
    );
  }

  // Requirement 14.2: Check revenue consistency
  // Allow small floating point differences (< $0.01)
  const revenueDifference = Math.abs(familyListData.monthlyRevenue - familyDetailData.monthlyRevenue);
  if (revenueDifference > 0.01) {
    warnings.push(
      `Monthly revenue mismatch for family ${familyListData.familyNumber}: ` +
      `list view shows $${familyListData.monthlyRevenue.toFixed(2)}, ` +
      `detail view shows $${familyDetailData.monthlyRevenue.toFixed(2)} ` +
      `(difference: $${revenueDifference.toFixed(2)})`
    );
  }

  // If detail view has monthly stats, check that current month matches
  if (familyDetailData.monthlyStats && Array.isArray(familyDetailData.monthlyStats) && familyDetailData.monthlyStats.length > 0) {
    const currentMonthRevenue = familyDetailData.monthlyStats[0].revenue;
    const detailRevenueDifference = Math.abs(currentMonthRevenue - familyDetailData.monthlyRevenue);
    
    if (detailRevenueDifference > 0.01) {
      warnings.push(
        `Detail view revenue inconsistency: monthlyRevenue ($${familyDetailData.monthlyRevenue.toFixed(2)}) ` +
        `does not match current month in monthlyStats ($${currentMonthRevenue.toFixed(2)})`
      );
    }
  }

  // Requirement 14.3: Check active subject count consistency
  if (familyListData.totalActiveSubjects !== familyDetailData.totalActiveSubjects) {
    warnings.push(
      `Active subject count mismatch for family ${familyListData.familyNumber}: ` +
      `list view shows ${familyListData.totalActiveSubjects}, detail view shows ${familyDetailData.totalActiveSubjects}`
    );
  }

  // Calculate actual active subjects from detail view tutees
  if (familyDetailData.tutees && Array.isArray(familyDetailData.tutees)) {
    const calculatedActiveSubjects = familyDetailData.tutees.reduce((sum, tutee) => {
      if (tutee.subjects && Array.isArray(tutee.subjects)) {
        const activeCount = tutee.subjects.filter(s => s.isActive === true).length;
        return sum + activeCount;
      }
      return sum;
    }, 0);

    if (calculatedActiveSubjects !== familyDetailData.totalActiveSubjects) {
      warnings.push(
        `Detail view active subject count (${familyDetailData.totalActiveSubjects}) ` +
        `does not match calculated count from subjects (${calculatedActiveSubjects})`
      );
    }
  }

  // Calculate actual active subjects from list view tutees
  if (familyListData.tutees && Array.isArray(familyListData.tutees)) {
    const calculatedActiveSubjects = familyListData.tutees.reduce((sum, tutee) => {
      return sum + (tutee.activeSubjects || 0);
    }, 0);

    if (calculatedActiveSubjects !== familyListData.totalActiveSubjects) {
      warnings.push(
        `List view active subject count (${familyListData.totalActiveSubjects}) ` +
        `does not match sum of tutees' active subjects (${calculatedActiveSubjects})`
      );
    }
  }

  return {
    isConsistent: warnings.length === 0,
    warnings
  };
};

/**
 * Validate sibling parent data consistency
 * Ensures all siblings in a family have identical parent information
 * 
 * @param {object} familyData - Family data with tutees array
 * @returns {object} { isConsistent: boolean, warnings: string[] }
 */
export const validateSiblingParentConsistency = (familyData) => {
  const warnings = [];

  // Validate input
  if (!familyData || typeof familyData !== 'object') {
    warnings.push('Family data is invalid or missing');
    return { isConsistent: false, warnings };
  }

  if (!familyData.tutees || !Array.isArray(familyData.tutees)) {
    warnings.push('Tutees array is invalid or missing');
    return { isConsistent: false, warnings };
  }

  // Only check if there are multiple tutees (siblings)
  if (familyData.tutees.length <= 1) {
    return { isConsistent: true, warnings: [] };
  }

  // Get expected parent information from family level
  const expectedParentName = familyData.parentName;
  const expectedParentPhone = familyData.parentPhone;
  const expectedParentEmail = familyData.parentEmail;
  const expectedParentAddress = familyData.parentAddress;

  // Check each tutee's parent information (if it exists at tutee level)
  familyData.tutees.forEach((tutee, index) => {
    if (!tutee.parent) {
      // No parent data at tutee level, which is fine - family level data is used
      return;
    }

    // Requirement 14.4: Verify all siblings have identical parent info
    if (tutee.parent.name && tutee.parent.name !== expectedParentName) {
      warnings.push(
        `Tutee ${index} (${tutee.name || tutee._id}) has inconsistent parent name: ` +
        `expected "${expectedParentName}", got "${tutee.parent.name}"`
      );
    }

    if (tutee.parent.phone && tutee.parent.phone !== expectedParentPhone) {
      warnings.push(
        `Tutee ${index} (${tutee.name || tutee._id}) has inconsistent parent phone: ` +
        `expected "${expectedParentPhone}", got "${tutee.parent.phone}"`
      );
    }

    if (tutee.parent.email && expectedParentEmail && tutee.parent.email !== expectedParentEmail) {
      warnings.push(
        `Tutee ${index} (${tutee.name || tutee._id}) has inconsistent parent email: ` +
        `expected "${expectedParentEmail}", got "${tutee.parent.email}"`
      );
    }

    if (tutee.parent.address && expectedParentAddress && tutee.parent.address !== expectedParentAddress) {
      warnings.push(
        `Tutee ${index} (${tutee.name || tutee._id}) has inconsistent parent address: ` +
        `expected "${expectedParentAddress}", got "${tutee.parent.address}"`
      );
    }
  });

  return {
    isConsistent: warnings.length === 0,
    warnings
  };
};

/**
 * Log consistency warnings to console
 * 
 * @param {string} context - Context of the validation (e.g., "Family 101")
 * @param {object} validationResult - Result from validation function
 */
export const logConsistencyWarnings = (context, validationResult) => {
  if (!validationResult.isConsistent && validationResult.warnings.length > 0) {
    console.warn(`[Data Consistency Warning] ${context}:`);
    validationResult.warnings.forEach(warning => {
      console.warn(`  - ${warning}`);
    });
  }
};

/**
 * Create a data refresh event
 * Used to trigger refresh of all views when data is updated
 * 
 * @param {string} familyNumber - Family number that was updated
 * @param {object} updatedData - The updated family data
 */
export const createDataRefreshEvent = (familyNumber, updatedData) => {
  const event = new CustomEvent('familyDataRefresh', {
    detail: {
      familyNumber,
      updatedData,
      timestamp: Date.now()
    }
  });
  
  window.dispatchEvent(event);
};

/**
 * Listen for data refresh events
 * Components can use this to refresh their data when updates occur
 * 
 * @param {function} callback - Function to call when refresh event occurs
 * @returns {function} Cleanup function to remove event listener
 */
export const onDataRefresh = (callback) => {
  const handler = (event) => {
    callback(event.detail);
  };
  
  window.addEventListener('familyDataRefresh', handler);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('familyDataRefresh', handler);
  };
};

/**
 * Validate that data refresh propagates within acceptable time
 * 
 * @param {function} triggerRefresh - Function that triggers the data refresh
 * @param {function} checkRefreshed - Function that returns true if data is refreshed
 * @param {number} maxTimeMs - Maximum time in milliseconds (default 1000ms)
 * @returns {Promise<object>} { success: boolean, timeMs: number, error?: string }
 */
export const validateRefreshPropagation = async (triggerRefresh, checkRefreshed, maxTimeMs = 1000) => {
  const startTime = Date.now();
  
  try {
    // Trigger the refresh
    await triggerRefresh();
    
    // Poll for refresh completion
    const pollInterval = 50; // Check every 50ms
    let elapsed = 0;
    
    while (elapsed < maxTimeMs) {
      if (checkRefreshed()) {
        const timeMs = Date.now() - startTime;
        return { success: true, timeMs };
      }
      
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      elapsed = Date.now() - startTime;
    }
    
    // Timeout
    return {
      success: false,
      timeMs: Date.now() - startTime,
      error: `Data refresh did not propagate within ${maxTimeMs}ms`
    };
  } catch (error) {
    return {
      success: false,
      timeMs: Date.now() - startTime,
      error: error.message
    };
  }
};
